import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ProjectsTopSection from 'components/modules/project-management/ProjectsTopSection';
import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Pagination, Row } from 'react-bootstrap';
import { CircularProgress, Box } from '@mui/material';
import { useGetProjectListsQuery } from '../project-details/data-access/project.api';
import ProjectCard from './ProjectCard';
import { Project } from '@app/shared/types/project.type';

const PAGE_SIZE = 12;

interface ProjectListViewProps {
  basePath?: string;
  createProjectPath?: string;
}

const ProjectListView = ({ basePath = '/project', createProjectPath }: ProjectListViewProps) => {
  const userId = localStorage.getItem('userId') || '';
  const [environment, setEnvironment] = useState(localStorage.getItem('SELECTED_ENV') || 'DEV');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const handleStorageChange = () => {
      const newEnv = localStorage.getItem('SELECTED_ENV') || 'DEV';
      setEnvironment(prev => (prev !== newEnv ? newEnv : prev));
    };
    const interval = setInterval(handleStorageChange, 500);
    handleStorageChange();
    return () => clearInterval(interval);
  }, []);

  const organizationId = localStorage.getItem('organizationId') || '';

  const {
    data: projectList = [],
    isLoading,
    isFetching
  } = useGetProjectListsQuery(
    { userId, environment, organizationId },
    {
      skip: !userId || !organizationId,
      refetchOnMountOrArgChange: true
    }
  );

  const { paginatedProjects, totalPages } = useMemo(() => {
    const list = projectList ?? [];
    const total = Math.ceil(list.length / PAGE_SIZE) || 1;
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return {
      paginatedProjects: list.slice(start, end) as Project[],
      totalPages: total
    };
  }, [projectList, currentPage]);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const showLoading = isLoading || isFetching;

  return (
    <div>
      <div className="d-flex flex-wrap mb-4 gap-3 gap-sm-6 align-items-center">
        <h2 className="mb-0">
          <span className="me-3">Projects</span>
          <span className="fw-normal text-body-tertiary">({showLoading ? '...' : projectList?.length || 0})</span>
        </h2>
        {userId && (
          <Link className="btn btn-primary px-5" to={createProjectPath ?? `${basePath}/${userId}/create-project`}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add new project
          </Link>
        )}
      </div>

      <ProjectsTopSection activeView="card" />

      {showLoading ? (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height={200} gap={2}>
          <CircularProgress size={50} />
          <p className="text-muted">{isFetching ? 'Loading projects...' : 'Fetching projects...'}</p>
        </Box>
      ) : projectList && projectList.length > 0 ? (
        <>
          <Row>
            {paginatedProjects.map(project => (
              <ProjectCard key={project.projectId} project={project} basePath={basePath} />
            ))}
          </Row>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination className="mb-0">
                <Pagination.First
                  onClick={e => {
                    e.preventDefault();
                    setCurrentPage(1);
                  }}
                  disabled={currentPage <= 1}
                  href="#"
                />
                <Pagination.Prev
                  onClick={e => {
                    e.preventDefault();
                    setCurrentPage(p => Math.max(1, p - 1));
                  }}
                  disabled={currentPage <= 1}
                  href="#"
                />
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => setCurrentPage(page)}
                    style={{ cursor: 'pointer' }}
                  >
                    {page}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  onClick={e => {
                    e.preventDefault();
                    setCurrentPage(p => Math.min(totalPages, p + 1));
                  }}
                  disabled={currentPage >= totalPages}
                  href="#"
                />
                <Pagination.Last
                  onClick={e => {
                    e.preventDefault();
                    setCurrentPage(totalPages);
                  }}
                  disabled={currentPage >= totalPages}
                  href="#"
                />
              </Pagination>
            </div>
          )}
        </>
      ) : (
        <p className="text-center text-muted py-5">No projects found.</p>
      )}
    </div>
  );
};

export default ProjectListView;
